import React, { memo, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'

import { Typography, makeStyles, Box, Button, CircularProgress, Grid } from '@material-ui/core'

import {
    CheckoutForm,
    SpacedGroup,
    ApplyCoupon,
    CouponResDataType,
    ApplyCouponProps
} from 'UI/Components'

import { openLink, formatDate, logToAnalytics } from 'modules'
import { BillingPeriod } from '@stigg/js-client-sdk'
import { Elements } from '@stripe/react-stripe-js'
import { SetupIntentResult, Stripe } from '@stripe/stripe-js'

import {
    BucketType,
    EstimateSubscriptionInput,
    MappedPlanType,
    roundPrice,
    SubmitStripeFormType,
    EstimateSubscriptionResType,
    useBoolean,
    useNotification,
    viewTermsLink
} from 'hooks'
import { closePlanDialogs } from 'ducks'
import { SCREEN } from './helpers'

const useStyles = makeStyles(theme => ({
    title: {
        fontSize: '24px',
        fontWeight: theme.fontWeightBold,
        textAlign: 'center'
    },
    planItem: {
        overflow: 'hidden',
        position: 'relative',
        display: 'flex',
        borderRadius: '6px',
        width: `calc(100% - ${theme.spacing(4)}px)`,
        margin: theme.spacing(0, 2),
        border: '3px solid #26c6da',
        minHeight: '520px'
    },
    recommended: {
        position: 'absolute',
        left: 0,
        top: theme.spacing(1),
        color: '#fff',
        padding: '7px 20px 5px 7px',
        background: '#26c6da'
    },
    square: {
        position: 'absolute',
        width: '22px',
        height: '22px',
        right: '-20px',
        top: '50%',
        background: '#fff',
        transform: 'translate(50%, -50%) rotate(45deg)'
    },
    price: {
        fontSize: '20px',
        fontWeight: theme.fontWeightBold,
        textAlign: 'center',
        color: '#26c6da',
        marginTop: theme.spacing(2)
    },
    info: {
        marginTop: theme.spacing(1),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'relative'
    },
    bold: {
        fontWeight: theme.fontWeightBold
    },
    infoButton: {
        color: '#26c6da',
        fontWeight: 500,
        padding: '0',
        margin: '0',
        lineHeight: 'normal',
        background: 'none !important',
        minWidth: 'auto',
        marginLeft: '8px'
    },
    grey: {
        color: '#78909c'
    },
    terms: {
        color: '#26c6da',
        cursor: 'pointer'
    },
    secondaryText: {
        color: '#6d6e78',
        fontSize: '12px'
    }
}))

type Props = {
    bucket: BucketType
    setStep: React.Dispatch<React.SetStateAction<number>>
    isLoading: boolean
    confirmPlanUpdate: () => void
    submitStripeForm: SubmitStripeFormType
    stripePromise: Stripe | null
    clientSecret?: string | null
    selectedPlan?: MappedPlanType
    estimateSubscriptionData?: EstimateSubscriptionResType
    couponId?: string
    onError?: (res: SetupIntentResult) => void
    estimateSubscription: (input: EstimateSubscriptionInput) => void
    isEstimationLoading: boolean
    applyCoupon: (input: ApplyCouponProps) => Promise<CouponResDataType>
    couponResData: CouponResDataType
    isCouponApplying: boolean
}

export const Payment = memo(
    ({
        bucket,
        setStep,
        isLoading,
        confirmPlanUpdate,
        clientSecret,
        submitStripeForm,
        stripePromise,
        selectedPlan,
        onError,
        estimateSubscriptionData,
        estimateSubscription,
        isEstimationLoading,
        applyCoupon,
        couponResData,
        isCouponApplying
    }: Props) => {
        const classes = useStyles()
        const isFirstInit = useBoolean()
        const dispatch = useDispatch()
        const { showSuccessNotification } = useNotification()
        const isCouponLoading = useBoolean(true)
        const addressReady = useBoolean()
        const addressShown = useBoolean()

        const [promotionCode, setPromotionCode] = useState('')
        const [country, setCountry] = useState('')

        const { period, totalLicenses, planId } = bucket

        const paramsToEstimate = useMemo(
            () => ({
                planId,
                billingPeriod: period,
                unitQuantity: totalLicenses,
                ...(promotionCode && { promotionCode }),
                ...(country && addressShown.isTrue && { country })
            }),
            [addressShown.isTrue, country, period, planId, promotionCode, totalLicenses]
        )

        useEffect(() => {
            if (isFirstInit.isTrue) return

            if (!isLoading) {
                isFirstInit.setTrue()
                isCouponLoading.setFalse()
            }
        }, [isLoading, isFirstInit, isCouponLoading])

        useEffect(() => {
            estimateSubscription(paramsToEstimate)
        }, [estimateSubscription, paramsToEstimate])

        if (!selectedPlan) return null

        const { subscription, discount, tax } = estimateSubscriptionData || {}
        const { metadata, planName } = selectedPlan
        const subTotal = roundPrice(subscription?.subTotal.amount || 0)
        const totalPrice = roundPrice(subscription?.total.amount || 0)
        const nextBillingDate = subscription?.billingPeriodRange.end
        const pricePerUnitMonthly = roundPrice(
            period === BillingPeriod.Annually ? subTotal / 12 : subTotal
        )

        return (
            <Grid container spacing={8} className={classes.planItem} justifyContent="center">
                <Grid
                    item
                    xs={clientSecret ? 6 : 12}
                    style={{ position: 'relative', display: 'flex', alignItems: 'stretch' }}
                >
                    <Box
                        maxWidth="400px"
                        mx="auto"
                        width="100%"
                        display="flex"
                        flexDirection="column"
                    >
                        {metadata.isRecommended && (
                            <Box className={classes.recommended}>
                                <Box position="relative">
                                    <Box className={classes.square} />
                                    <Typography
                                        variant="body2"
                                        style={{
                                            fontWeight: 700
                                        }}
                                    >
                                        Recommended
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                        <Typography
                            data-cy={`${planName}-plan`}
                            className={classes.title}
                            style={{ marginTop: clientSecret ? '16px' : 0 }}
                        >
                            {planName}
                        </Typography>
                        {(isLoading && isFirstInit.isFalse) ||
                        !subscription ||
                        isEstimationLoading ? (
                            <Box
                                style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate3d(-50%, -50%, 0px)'
                                }}
                            >
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Box
                                flexGrow="1"
                                display="flex"
                                flexDirection="column"
                                justifyContent="space-between"
                            >
                                <Box>
                                    <Typography
                                        data-cy="payment-amountPerPeriod"
                                        className={classes.price}
                                    >
                                        ${totalPrice} / {period.toLowerCase()}
                                    </Typography>
                                    <Box>
                                        <Box className={classes.info}>
                                            <Box data-cy="payment-plan" mr={2}>
                                                Plan (per seat)
                                            </Box>
                                            <Box data-cy="payment-plan-data">
                                                {period === BillingPeriod.Monthly ? (
                                                    <b>
                                                        $
                                                        {roundPrice(
                                                            pricePerUnitMonthly / totalLicenses
                                                        )}
                                                    </b>
                                                ) : (
                                                    <>
                                                        $
                                                        {roundPrice(
                                                            pricePerUnitMonthly / totalLicenses
                                                        )}{' '}
                                                        x 12 months ={' '}
                                                        <b>
                                                            $
                                                            {roundPrice(
                                                                (pricePerUnitMonthly /
                                                                    totalLicenses) *
                                                                    12
                                                            )}
                                                        </b>
                                                    </>
                                                )}
                                            </Box>
                                        </Box>
                                        <Box className={classes.info}>
                                            <Box data-cy="payment-total-seats" mr={2}>
                                                Total seats{' '}
                                            </Box>
                                            <Box
                                                data-cy="payment-total-seats-data"
                                                className={classes.bold}
                                                display="flex"
                                                alignItems="center"
                                            >
                                                {totalLicenses} seats
                                                <Button
                                                    data-cy="payment-change-button"
                                                    className={classes.infoButton}
                                                    variant="text"
                                                    disableRipple
                                                    onClick={() => setStep(SCREEN.SelectSeats)}
                                                >
                                                    Change
                                                </Button>
                                            </Box>
                                        </Box>
                                        {discount && (
                                            <Box className={classes.info}>
                                                <Box mr={2}>Discount</Box>
                                                <Box className={classes.bold}>
                                                    -${discount.amount} ({discount.percent}% off)
                                                </Box>
                                            </Box>
                                        )}
                                        {tax && (
                                            <Box className={classes.info}>
                                                <Box mr={2}>VAT (+{tax.percent}%)</Box>
                                                <Box className={classes.bold}>${tax.amount}</Box>
                                            </Box>
                                        )}
                                        <Box className={classes.info}>
                                            <Box data-cy="payment-starting-today" mr={2}>
                                                Starting today
                                            </Box>
                                            <Box
                                                data-cy="payment-starting-today-data"
                                                className={classes.bold}
                                            >
                                                ${totalPrice} / {period.toLowerCase()}
                                            </Box>
                                        </Box>
                                        {nextBillingDate && (
                                            <Box className={classes.info}>
                                                <Box data-cy="payment-next-billing" mr={2}>
                                                    Next billing
                                                </Box>
                                                <Box
                                                    data-cy="payment-next-billing-data"
                                                    className={classes.bold}
                                                >
                                                    {formatDate(
                                                        Math.round(
                                                            +new Date(nextBillingDate) / 1000
                                                        )
                                                    )}
                                                </Box>
                                            </Box>
                                        )}
                                        <Box mt={1} position="relative">
                                            <ApplyCoupon
                                                applyCoupon={applyCoupon}
                                                promoCode={couponResData?.couponId || ''}
                                                isCouponApplying={isCouponApplying}
                                                onReqStart={isCouponLoading.setTrue}
                                                onReqEnd={isCouponLoading.setFalse}
                                                onSuccess={data =>
                                                    setPromotionCode(data?.couponId || '')
                                                }
                                                planId={planId}
                                                billingPeriod={period}
                                                unitQuantity={totalLicenses}
                                            />
                                        </Box>

                                        {!clientSecret && (
                                            <>
                                                <Box mt={4} textAlign="center">
                                                    <Button
                                                        onClick={confirmPlanUpdate}
                                                        disabled={isLoading}
                                                    >
                                                        Upgrade plan
                                                    </Button>
                                                </Box>
                                                <SpacedGroup
                                                    spacing={1}
                                                    mt={2}
                                                    justifyContent="center"
                                                >
                                                    <Typography
                                                        data-cy="payment-cancelAnyTime-text"
                                                        className={classes.grey}
                                                        variant="caption"
                                                    >
                                                        Cancel any time.
                                                    </Typography>
                                                    <Typography
                                                        data-cy="payment-viewTerms-text"
                                                        variant="caption"
                                                        className={classes.terms}
                                                        onClick={() => {
                                                            logToAnalytics('open_view_terms')
                                                            openLink(viewTermsLink)
                                                        }}
                                                    >
                                                        View terms
                                                    </Typography>
                                                </SpacedGroup>
                                            </>
                                        )}
                                    </Box>
                                </Box>
                                <Typography
                                    data-cy="payment-taxes-text"
                                    className={classes.secondaryText}
                                >
                                    Taxes may be applied based on your selected country
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Grid>

                {clientSecret && (
                    <Grid
                        item
                        xs={6}
                        style={{
                            boxShadow: '15px 0 30px 0 rgb(0 0 0 / 18%)',
                            display: 'flex',
                            alignItems: 'stretch'
                        }}
                    >
                        <Box maxWidth="400px" mx="auto" display="flex" alignItems="stretch">
                            <Elements
                                stripe={stripePromise}
                                options={{ clientSecret, loader: 'never' }}
                            >
                                <CheckoutForm
                                    submitBtnName="Subscribe"
                                    submitStripeForm={submitStripeForm}
                                    isLoading={isLoading}
                                    onError={onError}
                                    onSuccess={() => {
                                        dispatch(closePlanDialogs())
                                        showSuccessNotification(
                                            'We’ve received your request. Your plan will be changed shortly.'
                                        )
                                    }}
                                    onAddressChange={event => {
                                        setCountry(event.value.address.country)
                                    }}
                                    onAddressVisibilityChanged={addressShown.set}
                                    onAddressReady={addressReady.setTrue}
                                />
                            </Elements>
                        </Box>
                    </Grid>
                )}
            </Grid>
        )
    }
)
